import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  from,
} from '@apollo/client';
import {onError} from '@apollo/client/link/error';
import {
  offsetLimitPagination,
  relayStylePagination,
} from '@apollo/client/utilities';
import {toError} from '@ark/react';
import {PostHogProvider} from '@posthog/react';
import {UserProvider, userContext} from '@ark/web';
import * as Sentry from '@sentry/react';
import {CachePersistor, LocalStorageWrapper} from 'apollo3-cache-persist';
import {useContext, useEffect, useMemo, useState} from 'react';
import * as ReactDOM from 'react-dom/client';
import {
  Routes,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import {IntercomProvider} from 'react-use-intercom';
import {ClarityProvider} from 'use-clarity';
import App from './App';
import SnackbarComponent from './components/feedback/Snackbar';
import {possibleTypes} from './possibleTypes';
import * as serviceWorker from './serviceWorker';
import {runLoginHandoff} from './loginHandoff';
import posthog from './posthog';

const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_ID;
const CLARITY_ID = import.meta.env.VITE_CLARITY_ID;

const mergeIfSameUrl = (
  existing: string | null | undefined,
  incoming: string | null,
): string | null => {
  if (
    typeof existing === 'string' &&
    typeof incoming === 'string' &&
    existing.split('?')[0] == incoming.split('?')[0]
  ) {
    return existing;
  }
  return incoming;
};

const cache = new InMemoryCache({
  possibleTypes,
  typePolicies: {
    PortfolioEngagementReportingNode: {merge: true},
    PortfolioMaintenanceReportingNode: {merge: true},
    PortfolioSatisfactionReportingNode: {merge: true},
    Query: {
      fields: {
        listConversations: offsetLimitPagination([
          'communityId',
          'urgency',
          'managerId',
          'unresolved',
          'residentId',
          'sortBy',
          'assignedTo',
          'unanswered',
        ]),
        conversations: relayStylePagination([
          'community',
          'manager',
          'search',
          'unresolved',
          'resident',
          'isUrgent',
          'orderBy',
          'hasResponseTime',
          'communities',
          'status',
          'date',
          'home',
          'contact',
          'assignedTo',
          'assignedUsers',
          'unanswered',
          'dateFrom',
          'dateTo',
          'teams',
          'source',
          'clientTags',
          'block',
        ]),
        residents: relayStylePagination([
          'community',
          'manager',
          'search',
          'with_rating',
          'downloaded',
          'orderBy',
          'communities',
        ]),
        applications: relayStylePagination([
          'communities',
          'manager',
          'search',
          'orderBy',
          'status',
          'dateFrom',
          'dateTo',
        ]),
        packages: relayStylePagination([
          'communities',
          'manager',
          'status',
          'statuses',
          'search',
          'fullTextSearch',
          'orderBy',
          'locations',
          'dateFrom',
          'dateTo',
          'block',
        ]),
        keys: relayStylePagination([
          'communities',
          'search',
          'search2',
          'hasCurrentKeyholder',
          'status',
          'orderBy',
          'type',
          'dateFrom',
          'dateTo',
          'block',
        ]),
        residencies: relayStylePagination(['home']),
        complianceRequirements: relayStylePagination([
          'orderBy',
          'status',
          'search',
        ]),
        communities: relayStylePagination([
          'includeDeactivated',
          'orderBy',
          'status',
          'search',
          'manager',
          'isManager',
          'ids',
        ]),
        communityProducts: relayStylePagination([
          'communities',
          'active',
          'orderBy',
          'search',
        ]),
        homes: relayStylePagination([
          'communities',
          'icontainsSearch',
          'trigramSearch',
          'contact',
          'community',
          'hasBlock',
          'block',
          'includeDemoHomes',
        ]),
        communityUpdates: relayStylePagination([
          'communities',
          'communityGroups',
          'search',
          'status',
          'category',
          'orderBy',
        ]),
        clientUsers: relayStylePagination([
          'search',
          'communities',
          'teams',
          'orderBy',
        ]),
        clientStaffMembers: relayStylePagination([
          'search',
          'communities',
          'teams',
          'excludeTeams',
          'orderBy',
        ]),
        surveys: relayStylePagination([
          'communities',
          'communityGroups',
          'search',
          'orderBy',
        ]),
        notifications: relayStylePagination([
          'read',
          'platform',
          'hasTarget',
          'type',
        ]),
        leanNotifications: relayStylePagination([
          'platform',
          'read',
          'archivedAt',
          'hasTarget',
          'hasActor',
          'type',
        ]),
        contacts: relayStylePagination([
          'search',
          'community',
          'communities',
          'home',
          'downloaded',
          'orderBy',
        ]),
        communityDocuments: relayStylePagination(['communities', 'tags']),
        communityEvents: relayStylePagination([
          'communities',
          'orderBy',
          'search',
          'status',
        ]),
        leaseholderPayments: relayStylePagination(['search', 'communities']),
        invoices: relayStylePagination(['type']),
        amenities: relayStylePagination([
          'communities',
          'type',
          'search',
          'orderBy',
        ]),
        amenityReservations: relayStylePagination([
          'amenity',
          'toDate',
          'fromDate',
        ]),
        communityGroups: relayStylePagination([
          'search',
          'communities',
          'orderBy',
          'community',
          'isAutomatic',
          'active',
          'groupType',
        ]),
        tenantReferences: relayStylePagination(['hasBeenSent']),
        tenancyApplications: relayStylePagination([
          'search',
          'status',
          'communities',
          'hasBeenSent',
        ]),
        communityVisitors: relayStylePagination([
          'search',
          'communities',
          'type',
          'dateFrom',
          'dateTo',
          'block',
        ]),
        documents: relayStylePagination([
          'search',
          'communities',
          'communityGroups',
          'home',
          'type',
          'tags',
          'clientTags',
          'orderBy',
          'tags2',
          'includeArchived',
        ]),
        marketplaceItems: relayStylePagination([
          'search',
          'communities',
          'status',
          'orderBy',
        ]),
        blocks: relayStylePagination([
          'search',
          'communities',
          'hasRequirements',
          'orderBy',
        ]),
        blockComplianceRequirements: relayStylePagination([
          'search',
          'blocks',
          'status',
        ]),
        communityForms: relayStylePagination([
          'communities',
          'communityGroups',
          'orderBy',
          'status',
        ]),
        teams: relayStylePagination([
          'status',
          'communities',
          'search',
          'orderBy',
        ]),
        clientTags: relayStylePagination(['status', 'search']),
        tasks: relayStylePagination([
          'search',
          'statuses',
          'orderBy',
          'community',
        ]),
        documentExtractions: relayStylePagination([
          'community',
          'communities',
          'mode',
          'status',
          'search',
          'complianceTypes',
          'orderBy',
        ]),
        clientContractorCompanies: relayStylePagination([
          'search',
          'orderBy',
          'communities',
          'isActive',
        ]),
        residentSafetyRecords: relayStylePagination([
          'communities',
          'community',
          'isOccupant',
          'hasConsent',
          'hasNeeds',
          'consentStatus',
          'pcfraStatus',
          'eesStatus',
          'search',
        ]),
        safetyRoster: {
          keyArgs: [
            'communities',
            'block',
            'cohort',
            'search',
            'pcfraStatus',
            'eesStatus',
          ],
          merge(existing, incoming, {args}) {
            const offset = args?.offset ?? 0;
            const existingRows = existing?.rows ?? [];
            const rows =
              offset === 0
                ? [...(incoming.rows ?? [])]
                : [...existingRows.slice(0, offset), ...(incoming.rows ?? [])];
            return {...incoming, rows};
          },
        },
      },
    },
    CommunityTeamMembersGroupType: {
      keyFields: ['communityId'],
    },
    MarketplaceItemNode: {
      fields: {
        comments: relayStylePagination([]),
      },
    },
    CommunityProductNode: {
      fields: {
        purchases: relayStylePagination([]),
      },
    },
    KeyNode: {
      fields: {
        keyholders: relayStylePagination(['isActive']),
        events: relayStylePagination([]),
      },
    },
    CommunityUpdateType: {
      fields: {
        comments: relayStylePagination([]),
      },
    },
    ApplicationNode: {
      fields: {
        applicationAttempts: relayStylePagination([]),
      },
    },
    CommunityNode: {
      fields: {
        team: relayStylePagination([]),
        communityGroups: relayStylePagination(['orderBy']),
      },
    },
    CommunityGroupNode: {
      fields: {
        users: relayStylePagination([]),
        homes: relayStylePagination([]),
      },
    },
    CommunityEventNode: {
      fields: {
        guests: relayStylePagination([]),
        parties: relayStylePagination([]),
      },
    },
    ResidencyDocumentNode: {
      fields: {
        views: relayStylePagination([]),
        url: {
          merge: mergeIfSameUrl,
        },
        file: {
          merge: mergeIfSameUrl,
        },
      },
    },
    CommunityDocumentNode: {
      fields: {
        views: relayStylePagination([]),
        url: {
          merge: mergeIfSameUrl,
        },
        file: {
          merge: mergeIfSameUrl,
        },
        groups: relayStylePagination([]),
      },
    },
    UserDocumentNode: {
      fields: {
        views: relayStylePagination([]),
        url: {
          merge: mergeIfSameUrl,
        },
        file: {
          merge: mergeIfSameUrl,
        },
      },
    },
    HomeNode: {
      fields: {
        packages: relayStylePagination([
          'communities',
          'manager',
          'status',
          'statuses',
          'search',
          'fullTextSearch',
          'orderBy',
        ]),
      },
    },
  },
});

const apiUrl = `${import.meta.env.VITE_API_ROOT}/api/graphql`;
const publicApiUrl = `${apiUrl}/public`;

type Props = {
  children: React.ReactNode;
};

export const createApolloClient = (
  authToken: string | null,
  onGQLError?: () => void,
) => {
  const uri = !authToken ? publicApiUrl : apiUrl;

  const headers: Record<string, string> = authToken
    ? {
        'X-Ark-Client': 'MANAGER_DASHBOARD',
        Authorization: `Token ${authToken}`,
      }
    : {'X-Ark-Client': 'MANAGER_DASHBOARD'};

  const httpLink = new HttpLink({
    uri,
    headers,
  });

  const errorLink = onError(({graphQLErrors, networkError, operation}) => {
    if (operation.getContext().handlesErrors) return;
    if (graphQLErrors || networkError) {
      onGQLError?.();
    }
  });

  return new ApolloClient({
    link: from([errorLink, httpLink]),
    cache,
    defaultOptions: {
      mutate: {
        awaitRefetchQueries: true,
      },
    },
    connectToDevTools: import.meta.env.VITE_ENVIRONMENT !== 'production',
  });
};

const AuthorizedApolloProvider = ({children}: Props) => {
  const {authToken} = useContext(userContext);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const client = useMemo(() => {
    return createApolloClient(authToken, () => setShowErrorSnackbar(true));
  }, [authToken, setShowErrorSnackbar]);

  return (
    <ApolloProvider client={client}>
      {children}
      <SnackbarComponent
        open={showErrorSnackbar}
        setClosed={() => setShowErrorSnackbar(false)}
        message="Something went wrong. Please try again."
        severity="error"
      />
    </ApolloProvider>
  );
};

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    tracesSampleRate:
      import.meta.env.VITE_ENVIRONMENT === 'production' ? 0.01 : 1,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    environment: import.meta.env.VITE_ENVIRONMENT,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        ),
      }),
      new Sentry.Replay({
        maskAllText: false,
        maskAllInputs: false,
        networkDetailAllowUrls: [apiUrl, publicApiUrl],
      }),
    ],
    maxValueLength: 10000,
    ignoreErrors: [
      /Loading chunk [\d]+ failed/,
      /Network request (failed|timed out)/,
      /Failed to fetch dynamically imported module/,
      /Importing a module script failed/,
      /NetworkError when attempting to fetch resource/,
      /^(?:(?:Type|Apollo)Error: )?(?:Failed to fetch|Load failed)$/,
      /messaging\/(?:unknown|unsupported-browser|permission-blocked)/,
      /ResizeObserver loop/,
      /Object Not Found Matching Id:\d+, MethodName:update/,
    ],
    replaysOnErrorSampleRate: 1,
    replaysSessionSampleRate: 0,
    tracePropagationTargets: [import.meta.env.VITE_API_ROOT],
  });
}
export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const CACHE_VERSION_KEY = 'apollo-cache-version';
const CURRENT_CACHE_VERSION = import.meta.env.VITE_SENTRY_RELEASE || '1';

export let cachePersistor: CachePersistor<NormalizedCacheObject>;

const withoutReportingFields = async (data: string) => {
  const extracted = JSON.parse(data);
  const rootQuery = extracted.ROOT_QUERY;
  if (!rootQuery) return data;
  return JSON.stringify({
    ...extracted,
    ROOT_QUERY: Object.fromEntries(
      Object.entries(rootQuery).filter(
        ([field]) => !field.startsWith('reporting'),
      ),
    ),
  });
};

const restoreCache = async () => {
  cachePersistor = new CachePersistor({
    cache,
    storage: new LocalStorageWrapper(window.localStorage),
    maxSize: 1048576 * 4,
    trigger: 'write',
    persistenceMapper: withoutReportingFields,
  });

  const currentVersion = localStorage.getItem(CACHE_VERSION_KEY);
  const hasAuthToken = !!localStorage.getItem('__A__');

  if (currentVersion === CURRENT_CACHE_VERSION && hasAuthToken) {
    await cachePersistor.restore();
  } else {
    await cachePersistor.purge();
    localStorage.setItem(CACHE_VERSION_KEY, CURRENT_CACHE_VERSION);
  }
};

const initApp = async () => {
  const handoff = await runLoginHandoff();
  if (handoff === 'redirecting') return;
  if (handoff === 'redeemed') localStorage.removeItem(CACHE_VERSION_KEY);

  try {
    await restoreCache();
  } catch (error) {
    Sentry.captureException(toError(error));
  }

  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement,
  );

  root.render(
    <PostHogProvider client={posthog}>
      <UserProvider>
        <AuthorizedApolloProvider>
          <ClarityProvider clarityId={CLARITY_ID}>
            <IntercomProvider appId={INTERCOM_APP_ID || ''}>
              <App />
            </IntercomProvider>
          </ClarityProvider>
        </AuthorizedApolloProvider>
      </UserProvider>
    </PostHogProvider>,
  );
  serviceWorker.unregister();
};

initApp();
